/**
 * Sample reliability populations and fits.
 *
 * Built so the screen must show what a failures-only fit would throw away:
 * the edge-light population is 400 units with 100 failures, so THREE QUARTERS
 * of the sample is censored. That is the number the module exists to keep, and
 * a mock where most units had failed would hide the whole point.
 *
 * One population is deliberately unfittable — a single unit, no failures — so
 * "cannot be fitted" has to render as its own state rather than as a fit of
 * zero.
 */
import type { Fit, Population } from "../domain/reliability";

export function mockPopulations(): Population[] {
  return [
    { population: "edge-light", timeUnit: "hours", units: 400, failures: 100, failureModes: 1, fittable: true },
    { population: "papi-lamp", timeUnit: "hours", units: 96, failures: 41, failureModes: 2, fittable: true },
    {
      population: "isolation-transformer",
      timeUnit: "hours",
      units: 1,
      failures: 0,
      failureModes: 0,
      fittable: false,
      whyNot: "one unit and no failures — there is nothing to fit, which is not the same as a long life",
    },
  ];
}

export function mockFits(population: string): Fit[] {
  if (population === "papi-lamp") {
    return [
      {
        id: "F-2",
        population,
        method: "weibull_mle",
        units: 96,
        failures: 41,
        censored: 55,
        beta: 1.08,
        betaLow: 0.82,
        betaHigh: 1.42,
        eta: 6200,
        timeUnit: "hours",
        lastFailureAt: 5400,
        fittedAt: new Date(Date.now() - 86400000).toISOString(),
        shapeVerdict:
          "beta = 1.08 with an interval spanning 1.0: this population does not demonstrably wear out, " +
          "and age-based replacement has nothing to work with here.",
        wearOut: {
          known: true,
          rejectsConstantRate: false,
          likelihoodRatio: 0.42,
          critical95: 3.841459,
          verdict:
            "The data do not reject a constant failure rate (LR = 0.42 < 3.84). Replacing these on age " +
            "would discard serviceable units without reducing failures.",
        },
      },
    ];
  }
  return [
    {
      id: "F-1",
      population: "edge-light",
      method: "weibull_mle",
      units: 400,
      failures: 100,
      /* 300 units still running — the ones a failures-only fit deletes */
      censored: 300,
      beta: 2.5548,
      betaLow: 2.1003,
      betaHigh: 3.0693,
      eta: 9777.5,
      timeUnit: "hours",
      lastFailureAt: 5987.6,
      fittedAt: new Date(Date.now() - 86400000).toISOString(),
      shapeVerdict:
        "beta = 2.55 with the whole interval above 1.0: this population wears out, so failures cluster " +
        "with age and age-based replacement has something to work with.",
      wearOut: {
        known: true,
        rejectsConstantRate: true,
        likelihoodRatio: 69.537,
        critical95: 3.841459,
        verdict:
          "The data reject a constant failure rate (LR = 69.54 > 3.84). Failures depend on age, so " +
          "age-based replacement has something to work with.",
        note:
          "A constant rate is what a Poisson count assumes. Where this test rejects it, a Poisson model " +
          "will under-predict late-life failures on this population.",
      },
    },
  ];
}
